import { change, getFormValues, isDirty, reset } from 'redux-form';
import { all, call, fork, put, select, takeLatest } from 'redux-saga/effects';
import callApi from 'utils/api';
import { indicators as types } from 'utils/app-configs';
import { referenceTypes } from '../../../utils/app-configs';
import * as actionTypes from './constants';
import * as actions from './create-order-actions';
import {
  checkIfRunning,
  getActionOrderDescription,
  getBusinessSubTypes,
  getCostPaidBys,
  getDescriptions,
  getDynamicValue,
  getFilteredIndicatorTypes,
  getFilteredOrderTypes,
  getIndicatorBusinessSubTypes,
  getIndicatorInputs,
  getIndicators,
  getPaidByValues,
} from './create-order-selectors';

export function* doInitializeOrder(action) {
  try {
    const result = yield call(callApi, {
      // eslint-disable-next-line
      endpoint: `order/wizard/${action.payload.type}/${action.payload.id}${action.payload.ticketActionId ? `?ticketActionId=${action.payload.ticketActionId}` : ''}`,
      options: {
        errorMessage: `Could not get ${action.payload.type} info: ${action.payload.id}`,
        retryAction: action,
      },
    });
    yield put(actions.initializeOrderSuccess(result));
  } catch (err) {
    yield put(actions.initializeOrderFailure(err));
  }
}

export function* doCreateOrder(action) {
  try {
    let request = action.payload;
    if (!action.payload.isSimplified) {
      const running = yield select(checkIfRunning);
      request = { ...action.payload, running };
    }
    const type = action.payload.type?.toLowerCase() || referenceTypes.equipment;
    if (type === referenceTypes.functionallocation) {
      request = { ...request, functionalLocationId: action.payload.id };
    } else {
      request = { ...request, equipmentId: action.payload.id };
    }

    const result = yield call(callApi, {
      endpoint: 'order/new',
      options: {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
        errorMessage: 'Could not create new order',
        retryAction: action,
      },
    });
    yield put(reset('newOrderForm'));
    yield put(actions.createOrderSuccess({
      ...result,
      assignedToAction: action.payload.assignedToAction || false,
    }));
  } catch (err) {
    yield put(actions.createOrderFailure(err));
  }
}

export function* setInitialValue(item) {
  const initialValue =
    item &&
    item.prefills &&
    item.prefills
      ?.filter(p => p.value)
      .reduce((p, c) => `${p} - ${c.value}`, '')
      .substr(3);
  yield put(change('newOrderForm', item.name, initialValue || ''));
}

/* eslint-disable complexity */
export function* doSetDefaults() {
  const formValues = yield select(getFormValues('newOrderForm'));
  const orderTypes = yield select(getFilteredOrderTypes);
  const descriptions = yield select(getDescriptions);
  const costPaidBys = yield select(getCostPaidBys);
  const paidByValues = yield select(getPaidByValues);
  const indicators = yield select(getIndicators);
  const dynamicValue = yield select(getDynamicValue);
  const orderDescription = yield select(getActionOrderDescription);
  const businessSubTypes = yield select(getBusinessSubTypes);
  if (orderTypes.length > 0 && (!formValues || !formValues.orderType)) {
    const defaultOrderType = orderTypes?.find(ot => ot.default);

    if (defaultOrderType) {
      yield put(change('newOrderForm', 'orderType', defaultOrderType.id));
    }
  }

  if (descriptions.length === 1 && !formValues.description) {
    yield put(change('newOrderForm', 'description', descriptions[0].id));
  }

  if (costPaidBys.length > 0 && !formValues.paidBy) {
    const defaultCostPaidBy = costPaidBys?.find(cpb => cpb.default);
    if (defaultCostPaidBy) {
      yield put(change('newOrderForm', 'paidBy', defaultCostPaidBy.id));

      // When preselecting paid by, try to preselect indicator as well
      if (
        defaultCostPaidBy.paidByValues &&
        defaultCostPaidBy.paidByValues.length > 0 &&
        !formValues.paidByValue
      ) {
        yield put(
          change(
            'newOrderForm',
            'paidByValue',
            defaultCostPaidBy.paidByValues?.find(cpb => cpb.default).id,
          ),
        );
      }
      if (defaultCostPaidBy.indicators.length === 1 && !formValues.indicator) {
        yield put(change('newOrderForm', 'indicator', defaultCostPaidBy.indicators[0].id));
      }
      if (defaultCostPaidBy.wizardInputs && defaultCostPaidBy.wizardInputs.length) {
        yield all(defaultCostPaidBy.wizardInputs?.map(item => call(setInitialValue, item)));
      }
    }
  }
  // On paidBy change, try to preselect paidByValue and indicator
  if (paidByValues.items.length > 0 && !formValues.paidByValue) {
    yield put(
      change('newOrderForm', 'paidByValue', paidByValues.items?.find(cpb => cpb.default).id),
    );
  }

  if (indicators.length === 1 && !formValues.indicator) {
    yield put(change('newOrderForm', 'indicator', indicators[0].id));
  }

  if (formValues?.indicator && businessSubTypes) {
    const defaultValue = businessSubTypes.find(v => v.isDefault);
    yield put(change('newOrderForm', 'businessSubType', defaultValue?.additionalParameter || null));
  } else {
    yield put(change('newOrderForm', 'businessSubType', null));
  }

  if (dynamicValue && dynamicValue.items.length === 1 && !formValues[dynamicValue.key]) {
    yield put(change('newOrderForm', dynamicValue.key, dynamicValue.items[0].id));
  }

  if (orderDescription && formValues) {
    // check length of the order type and cut the order description
    const maxLength = 37 - formValues.orderType.length;
    const shortenedOrderDescription = orderDescription.substr(0, maxLength);
    yield put(change('newOrderForm', 'description_custom', shortenedOrderDescription));
  }
}
/* eslint-enable complexity */

export function* checkForDefault(formValues, input) {
  if (formValues[input.name]) {
    return;
  }
  const defaultValue = input.values && input.values?.find(v => v.default);
  let valueDescription = '';
  if (defaultValue) {
    if (input.type === 'dropdown' && input.values.length === 1) {
      valueDescription = input.values[0].id;
    } else if (input.type !== 'dropdown') {
      if (defaultValue.id && defaultValue.description) {
        valueDescription = `${defaultValue.id} - ${defaultValue.description}`;
      } else if (defaultValue.id) {
        valueDescription = defaultValue.id;
      } else if (defaultValue.description) {
        valueDescription = defaultValue.description;
      } else {
        valueDescription = '';
      }
    } else if (input.type === 'dropdown') {
      valueDescription = defaultValue.description ? defaultValue.id : 'No description';
    }
    yield put(change('newOrderForm', input.name, valueDescription));
  }
}

export const getCheckIfRunning = state => state.order.create.machineIsRunning;

// eslint-disable-next-line complexity
export function* doSetIndicatorDefaults() {
  let formValues = yield select(getFormValues('newOrderForm'));
  const orderDescription = yield select(getActionOrderDescription);
  const filteredIndicatorTypes = yield select(getFilteredIndicatorTypes);
  const businessSubTypes = yield select(getIndicatorBusinessSubTypes);
  const isRunning = yield select(getCheckIfRunning);

  if (!formValues || !formValues.indicatorType) {
    let defaultIndicatorType = filteredIndicatorTypes?.find(it => it.default);
    const machineIsRunning =
      (!defaultIndicatorType ||
        !filteredIndicatorTypes?.find(type => type.id === defaultIndicatorType.id)) &&
      isRunning === 'no';
    if (machineIsRunning) {
      defaultIndicatorType = filteredIndicatorTypes?.find(it => it.id === types.doCharge);
    }
    if (defaultIndicatorType) {
      yield put(change('newOrderForm', 'indicatorType', defaultIndicatorType.id));
    } else if (filteredIndicatorTypes.length === 1) {
      yield put(change('newOrderForm', 'indicatorType', filteredIndicatorTypes[0].id));
    }
    formValues = yield select(getFormValues('newOrderForm'));
    if (formValues && formValues.indicatorType) {
      const indicatorInputs = yield select(getIndicatorInputs);
      if (indicatorInputs && indicatorInputs.length > 0) {
        yield all(indicatorInputs?.map(input => call(checkForDefault, formValues, input)));
      }
    }
    if (businessSubTypes?.length) {
      const defaultValue = businessSubTypes.find(v => v.isDefault);
      if (defaultValue) {
        yield put(change('newOrderForm', 'businessSubType', defaultValue.additionalParameter));
      }
    } else {
      yield put(change('newOrderForm', 'businessSubType', null));
    }
    if (orderDescription) {
      // check length of the order type and cut the order description
      const shortenedOrderDescription = orderDescription.substr(0, 40);
      yield put(change('newOrderForm', 'description_custom', shortenedOrderDescription));
    }
  } else {
    formValues = yield select(getFormValues('newOrderForm'));
    if (formValues && formValues.indicatorType) {
      const indicatorInputs = yield select(getIndicatorInputs);
      if (indicatorInputs && indicatorInputs.length > 0) {
        yield all(indicatorInputs?.map(input => call(checkForDefault, formValues, input)));
      }
      if (businessSubTypes?.length) {
        const defaultValue = businessSubTypes.find(v => v.isDefault);
        if (defaultValue) {
          yield put(change('newOrderForm', 'businessSubType', defaultValue.additionalParameter));
        }
      } else {
        yield put(change('newOrderForm', 'businessSubType', null));
      }
    }
    if (orderDescription && formValues) {
      // check length of the order type and cut the order description
      const shortenedOrderDescription = orderDescription.substr(0, 40);
      yield put(change('newOrderForm', 'description_custom', shortenedOrderDescription));
    }
  }
}

export const getUseIndicatorWizard = state => state.order.create.useIndicatorWizard;
export function* doAnswer() {
  const isFormDirty = yield select(isDirty('newOrderForm'));
  const useIndicatorWizard = yield select(getUseIndicatorWizard);
  if (isFormDirty) {
    yield put(reset('newOrderForm'));
  }
  if (useIndicatorWizard) {
    yield call(doSetIndicatorDefaults);
  } else {
    yield call(doSetDefaults);
  }
}

function* watchInitializeOrder() {
  yield takeLatest(actionTypes.INITIALIZE_ORDER, doInitializeOrder);
}

function* watchCreateOrder() {
  yield takeLatest(actionTypes.CREATE_ORDER, doCreateOrder);
}

function* watchAnswer() {
  yield takeLatest(actionTypes.ANSWER, doAnswer);
}

function* watchSetDefaults() {
  yield takeLatest(actionTypes.SET_DEFAULTS, doSetDefaults);
}

function* watchSetIndicatorDefaults() {
  yield takeLatest(actionTypes.SET_INDICATOR_DEFAULTS, doSetIndicatorDefaults);
}

export default [
  fork(watchInitializeOrder),
  fork(watchCreateOrder),
  fork(watchAnswer),
  fork(watchSetDefaults),
  fork(watchSetIndicatorDefaults),
];
